import {
  type FC,
  type RefObject,
  startTransition,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { cn } from '@/shared/react';
import { usePupeteerSignal } from '@/shared/usePupeteerSignal';
import { useCoinMarketDataOrdered } from '../../api';
import { CoinChartSection } from './CoinChartSection';
import { CoinHTMLSection } from './CoinHTMLSection';
import { CoinMarketDataSection } from './CoinMarketDataSection';
import { CoinPriceSection } from './CoinPriceSection';
import { CoinTabs } from './CoinTabs';
import { CoinTimeMachine } from './CoinTimeMachine';
import { VoteSection } from './VoteSection';

type CoinOrderedSectionsProps = {
  slug?: string;
  type: 'buy' | 'price';
};

export const CoinOrderedSections: FC<CoinOrderedSectionsProps> = ({
  slug,
  type,
}) => {
  const { data: coinMarketDataOrdered, isLoading: isCoinMarketDataLoading } =
    useCoinMarketDataOrdered(slug ?? '', type);
  usePupeteerSignal(!!coinMarketDataOrdered);

  const refsMapRef = useRef<Map<string, RefObject<HTMLElement>>>(new Map());

  const orderedSections = useMemo(() => {
    if (!coinMarketDataOrdered || coinMarketDataOrdered.length === 0) {
      return [];
    }

    return coinMarketDataOrdered?.map((section, index) => {
      const baseId = section.title?.trim() ?? '';
      const normalizedId = baseId.toLowerCase();
      const id = normalizedId || `section-${index}`;

      if (!refsMapRef.current.has(id)) {
        refsMapRef.current.set(id, { current: null as unknown as HTMLElement });
      }

      return {
        ...section,
        id,
        ref: refsMapRef.current.get(id)!,
      };
    });
  }, [coinMarketDataOrdered]);

  const tabs = useMemo(() => {
    return (
      orderedSections
        .filter((section) => section.show_in_tab)
        .map((section) => ({
          key: section.id,
          label: section.title ?? '',
          ref: section.ref as RefObject<HTMLElement>,
        })) || []
    );
  }, [orderedSections]);

  const [activeTab, setActiveTab] = useState(tabs[0]?.key);

  useEffect(() => {
    startTransition(() => {
      setActiveTab(tabs[0]?.key);
    });
    if (tabs.length === 0) return;

    const observers: IntersectionObserver[] = [];
    let rafId: number | null = null;

    const setupObservers = () => {
      tabs.forEach(({ key, ref }) => {
        const element = ref.current;
        if (!element) return;

        const observer = new IntersectionObserver(
          ([entry]) => {
            if (entry.isIntersecting) {
              rafId = requestAnimationFrame(() => {
                startTransition(() => {
                  setActiveTab(key);
                });
              });
            }
          },
          {
            rootMargin: '-40% 0px -40% 0px',
            threshold: 0.2,
          },
        );
        observer.observe(element);
        observers.push(observer);
      });
    };

    rafId = requestAnimationFrame(setupObservers);

    return () => {
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }
      observers.forEach((observer) => {
        observer.disconnect();
      });
    };
  }, [tabs]);

  const handleTabClick = (tab: {
    key: string;
    ref: RefObject<HTMLElement>;
  }) => {
    setActiveTab(tab.key);
    tab.ref.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  };

  return (
    <>
      {orderedSections.map((section) => {
        const baseClass = 'scroll-mt-28 py-5 px-3';

        if (section.type === 'market_data') {
          return (
            <section
              key={section.id}
              id={section.id}
              ref={section.ref}
              className={baseClass}
            >
              <CoinMarketDataSection slug={slug} />
            </section>
          );
        }

        if (section.type === 'tabs') {
          return (
            <section
              key={section.id}
              id={section.id}
              ref={section.ref}
              className={baseClass}
            >
              <CoinTabs
                tabs={tabs}
                activeTab={activeTab}
                onTabClick={handleTabClick}
              />
            </section>
          );
        }

        if (section.type === 'price') {
          return (
            <section
              key={section.id}
              id={section.id}
              ref={section.ref}
              className={baseClass}
            >
              <CoinPriceSection slug={slug} />
            </section>
          );
        }

        if (section.type === 'vote') {
          return (
            <section
              key={section.id}
              id={section.id}
              ref={section.ref}
              className={baseClass}
            >
              <VoteSection coinSymbol={slug} />
            </section>
          );
        }

        if (section.type === 'chart') {
          return (
            <section
              key={section.id}
              id={section.id}
              ref={section.ref}
              className={cn(
                baseClass,
                `flex flex-col gap-widget desktop:flex-row`,
              )}
            >
              <div className="w-full flex flex-col gap-widget">
                <CoinChartSection slug={slug} />
              </div>
            </section>
          );
        }

        if (section.type === 'time_machine') {
          return (
            <section
              key={section.id}
              id={section.id}
              ref={section.ref}
              className={baseClass}
            >
              <CoinTimeMachine slug={slug} />
            </section>
          );
        }

        if (section.type === 'html' || section.type === 'text') {
          const content = {
            type: section.type,
            data_base64: section.data_base64 ?? '',
          };

          return (
            <section
              key={section.id}
              id={section.id}
              ref={section.ref}
              className={cn(baseClass)}
            >
              <CoinHTMLSection
                content={content}
                loading={isCoinMarketDataLoading}
                slug={slug}
                heading={section.type === 'text' ? section.title : undefined}
              />
            </section>
          );
        }

        return null;
      })}
    </>
  );
};
